import { NavigateFunction } from 'react-router-dom';
import { Notification, CompetitionNotification } from '../types/notification';
import logger from './logger';

/**
 * Extract content info from notification - handles both modern and legacy formats (DRY)
 * Modern format: contentId, contentType, ownerId
 * Legacy format: eventReference.key, eventReference.type, eventReference.postOwnerUID
 */
const getContentInfo = (notification: Notification): { type?: string; id?: string; ownerId?: string } | null => {
  // Modern format - Phoenix style
  if ('contentId' in notification && 'contentType' in notification) {
    const ownerId = ('ownerId' in notification ? notification.ownerId :
                    ('senderId' in notification ? notification.senderId : undefined)) as string | undefined;
    return {
      type: notification.contentType as string,
      id: notification.contentId as string,
      ownerId
    };
  }

  // Legacy format - Original Marune Firebase functions style
  if ('eventReference' in notification && notification.eventReference) {
    const { type, key, postOwnerUID } = notification.eventReference;
    if (type && key) {
      return {
        type,
        id: key,
        ownerId: postOwnerUID || notification.eventInitiator
      };
    }
  }

  return null;
};

/**
 * Handle notification click navigation based on notification type
 * @param notification - The notification object
 * @param navigate - React Router navigate function
 * @param markAsRead - Optional function to mark notification as read
 * @param currentUserId - Optional current user ID (for exercise notifications, the recipient is the owner)
 */
export const handleNotificationClick = (
  notification: Notification,
  navigate: NavigateFunction,
  markAsRead?: (id: string) => void,
  currentUserId?: string
) => {
  // Mark as read if function provided
  if (markAsRead && !notification.read) {
    markAsRead(notification.id);
  }

  // Handle legacy notification types first
  const notificationType = notification.type as string;

  if (notificationType === 'requestReceived') {
    navigate('/friends/requests');
    return;
  }

  if (notificationType === 'requestAccepted' && 'eventInitiator' in notification && notification.eventInitiator) {
    // /profile/:username expects a username; eventInitiator is a uid — use the uid route
    navigate(`/user/${notification.eventInitiator}`);
    return;
  }

  // OSS = "On Some Shit" = Like notification (legacy format)
  // 'oss' notifs only received for own posts, so currentUserId IS the content owner
  if (notificationType === 'oss') {
    const contentInfo = getContentInfo(notification);
    if (contentInfo?.type && contentInfo?.id) {
      // For OSS, the recipient IS the content owner (currentUserId)
      // But if unavailable, let fetchPostById discover owner via publicFeed
      navigateToContent(contentInfo.type, contentInfo.id, navigate, '', currentUserId);
    } else {
      logger.warn('OSS notification missing contentInfo - cannot navigate');
    }
    return;
  }

  if (notificationType === 'friend-belt-prom' && 'eventInitiator' in notification && notification.eventInitiator) {
    // No per-user belt-progression route exists; show the friend's profile (uid-based route)
    navigate(`/user/${notification.eventInitiator}`);
    return;
  }

  if (notificationType === 'perk-unlocked') {
    navigate('/perks');
    return;
  }

  // Handle modern notification types (with legacy fallback via getContentInfo)
  switch (notification.type) {
    case 'like': {
      const contentInfo = getContentInfo(notification);
      if (contentInfo?.type && contentInfo?.id) {
        navigateToContent(contentInfo.type, contentInfo.id, navigate, '', contentInfo.ownerId);
      }
      break;
    }

    case 'comment': {
      const contentInfo = getContentInfo(notification);
      if (contentInfo?.type && contentInfo?.id) {
        const hash = 'commentId' in notification ? `#comment-${notification.commentId}` : '';
        navigateToContent(contentInfo.type, contentInfo.id, navigate, hash, contentInfo.ownerId);
      }
      break;
    }

    case 'tag': {
      const contentInfo = getContentInfo(notification);
      if (contentInfo?.type && contentInfo?.id) {
        navigateToContent(contentInfo.type, contentInfo.id, navigate, '', contentInfo.ownerId);
      }
      break;
    }

    case 'friendRequest':
      navigate('/friends/requests');
      break;

    case 'friendAccept':
    case 'newFollower':
      if ('senderId' in notification) {
        navigate(`/user/${notification.senderId}`);
      }
      break;

    case 'mention':
      if ('contentId' in notification && 'contentType' in notification) {
        if (notification.contentType === 'comment' || notification.contentType === 'post') {
          navigate(`/post/${notification.contentId}`);
        } else if (notification.contentType === 'training') {
          navigate(`/training/${notification.contentId}`);
        }
      }
      break;

    case 'promotion':
      // Navigate to the sender's belt progression page
      if ('senderId' in notification) {
        navigate(`/user/${notification.senderId}`);
      } else if ('eventInitiator' in notification && (notification as any).eventInitiator) {
        navigate(`/user/${(notification as any).eventInitiator}`);
      }
      break;

    case 'competition':
      if ('competitionId' in notification && notification.competitionId) {
        const competitionNotif = notification as CompetitionNotification;
        navigate(`/competitions/${competitionNotif.competitionId}`);
      }
      break;

    case 'system':
      if ('actionUrl' in notification && notification.actionUrl) {
        let url = notification.actionUrl;
        // Normalize legacy plural /clearmat/schools/ to singular /clearmat/school/
        url = url.replace('/clearmat/schools/', '/clearmat/school/');
        // Check if it's an internal or external URL
        if (url.startsWith('/')) {
          navigate(url);
        } else if (url.startsWith('http')) {
          window.open(url, '_blank');
        }
      }
      break;

    default:
      logger.warn('Unknown notification type:', notificationType);
  }
};

/**
 * Navigate to content based on type and ID
 * Handles both modern Phoenix types and legacy Firebase function types
 */
const navigateToContent = (
  contentType: string,
  contentId: string,
  navigate: NavigateFunction,
  hash?: string,
  ownerId?: string
) => {
  logger.info(`navigateToContent called: type=${contentType}, id=${contentId}, ownerId=${ownerId}`);
  switch (contentType) {
    // Training types (includes seminar, camp, teaching from Firebase)
    case 'training':
    case 'seminar':
    case 'camp':
    case 'teaching':
      navigate(`/training/${contentId}${hash || ''}`);
      break;

    case 'post':
    case 'topic':
      // Pass ownerId via state for topics (needed to fetch from /topics/{ownerId}/{topicId})
      navigate(`/post/${contentId}${hash || ''}`, {
        state: { ownerId, contentType }
      });
      break;

    case 'competition':
      navigate(`/competitions/${contentId}${hash || ''}`);
      break;

    case 'beltPromotion':
      // Belt promotions don't have a dedicated detail page - navigate to owner's belt progression
      if (ownerId) {
        navigate(`/user/${ownerId}`);
      } else {
        // Fallback: use PostDetailPage which can render belt promotions
        navigate(`/post/${contentId}${hash || ''}`, { state: { contentType: 'beltPromotion' } });
      }
      break;

    case 'exercise':
      // Exercise route requires ownerId: /exercise/:authorId/:exerciseId
      if (ownerId) {
        navigate(`/exercise/${ownerId}/${contentId}${hash || ''}`);
      } else {
        logger.warn('Missing owner ID for exercise navigation, redirecting to exercises list');
        navigate('/exercises');
      }
      break;

    case 'story':
      // Stories might have their own route or be part of feed
      if (ownerId) {
        navigate(`/story/${ownerId}/${contentId}${hash || ''}`);
      } else {
        navigate(`/story/${contentId}${hash || ''}`);
      }
      break;

    case 'workoutSession':
      if (ownerId) {
        navigate(`/workout-session/${ownerId}/${contentId}${hash || ''}`);
      } else {
        navigate(`/workout-sessions${hash || ''}`);
      }
      break;

    case 'workoutRoutine':
      navigate(`/workout-routine/${contentId}${hash || ''}`);
      break;

    default:
      // Fallback: try to navigate to a generic content page
      logger.warn('Unknown content type:', contentType, '- attempting generic navigation');
      navigate(`/content/${contentType}/${contentId}${hash || ''}`);
  }
};

/**
 * Get notification icon based on type
 */
export const getNotificationIcon = (type: string): string => {
  switch (type) {
    case 'like':
    case 'oss':
      return 'heart';
    case 'comment':
      return 'comment';
    case 'tag':
      return 'tag';
    case 'friendRequest':
    case 'requestReceived':
      return 'userPlus';
    case 'friendAccept':
    case 'requestAccepted':
      return 'userCheck';
    case 'newFollower':
      return 'user';
    case 'mention':
      return 'at';
    case 'promotion':
    case 'friend-belt-prom':
      return 'award';
    case 'competition':
      return 'medal';
    case 'perk-unlocked':
      return 'star';
    case 'system':
      return 'bell';
    default:
      return 'bell';
  }
};

/**
 * Get notification title for legacy types
 */
export const getNotificationTitle = (notification: Notification): string => {
  // Return existing title if present
  if (notification.title) {
    return notification.title;
  }

  // Generate title for legacy types
  const notificationType = notification.type as string;
  switch (notificationType) {
    case 'requestReceived':
      return 'Friend Request Received';
    case 'requestAccepted':
      return 'Friend Request Accepted';
    case 'oss':
      return 'Someone liked your post';
    case 'friend-belt-prom':
      return 'Friend Belt Promotion';
    case 'perk-unlocked':
      return 'Perk Unlocked!';
    default:
      return 'Notification';
  }
};

/**
 * Get notification body for legacy types
 */
export const getNotificationBody = (notification: Notification): string | undefined => {
  // Return existing body if present
  if (notification.body) {
    return notification.body;
  }

  // Use message field for legacy notifications
  if ('message' in notification && notification.message) {
    return notification.message;
  }

  // Generate body for specific types
  if ('eventReference' in notification && notification.eventReference?.postTitle) {
    return notification.eventReference.postTitle;
  }

  return undefined;
};